import Vue from 'vue'

// axios
import axios from 'axios'
import xhr from 'axios/unsafe/adapters/xhr.js'
import Dexie from 'dexie'
import sha256 from 'crypto-js/sha256'
import useJwt from '@/auth/jwt/useJwt'
import sLocalStorage from '@core/utils/secure-storage'
import router from '../router'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const axiosIns = axios.create({
  baseURL: process.env.VUE_APP_API_URL
})

// Dedup identical toasts within a 5-second window
const _recentToasts = {}
function showDedupedToast(key, toastFn, cooldownMs = 5000) {
  const now = Date.now()
  if (_recentToasts[key] && now - _recentToasts[key] < cooldownMs) return
  _recentToasts[key] = now
  toastFn()
}

const db = new Dexie('myDatabase')
db.version(1).stores({
  axiosCache: 'hash, result, expiresAt'
})

const CACHE_TTL = 2 * 60 * 1000 // 2 minutes
const cacheMethod = ['get']

const doNotCache = [
  'kunjungan',
  'kunjungan/\\d+',
  'laboratorium',
  'medicalrecord',
  'obat/search',
  'summary',
  'antrian_ruangan',
  'pasien',
  'penerimaan/\\d+/detail',
  'purchase-order',
  'opname',
  'buku-kas',
  'kasir-shifts', // Live shift totals + reconciliation report — a cached hit makes the reload button look dead
  'penerimaan/hutang',
  'tindakan',   // TODO: pagination dont invalidate properly
  'karyawan',   // TODO: pagination dont invalidate properly
  'dokter',     // No cache for doctor list (external doctors can be added dynamically)
  'Produsen',   // TODO: pagination dont invalidate properly
  'Merk',       // TODO: pagination dont invalidate properly
  'ProdukObat', // TODO: pagination dont invalidate properly
  'ruangan/smf',
  'ruangan/instalasi',
  'ruangan/dokter',
  'ruangan/medis',
  'ruangan/bed',
  'jadwal',
  'referensi/\\d+',  // Reference category lists (e.g. jenis racik 172, aturan minum 41) are editable master data — never serve a stale cached list
  'referensi/\\d+/\\d+(/ref_id)?/(enable|disable)',
  'satu-sehat',
  'rewards/points',
  'workspace',
  'general-consent', // Live signing status (pending→signed→revoked, re-sign) — never serve stale PDF link
  'paket-kunjungan',
  'radiologi',
  'Invoice',
  'rekening-bank',
  'penjamin-perusahaan',
  'pengklaiman',
  'superadmin',
  'mcu-template',
  'surat-template'
]

const cacheAdapter = (defaultAdapter) => async (config) => {
  const { method, headers } = config
  let url = config.url
  const param = config.params
  url = url + (param ? `?${(new URLSearchParams(param)).toString()}` : '')

  // Request Logic
  const urlWithoutParams = url.split('?')[0]
  for (const item of doNotCache) {
    if (new RegExp(item).test(urlWithoutParams)) {
      // console.log('Not Caching', urlWithoutParams, 'because of', item)
      return defaultAdapter(config)
    }
  }

  if (!cacheMethod.includes(method)) {
    // console.log('Not Caching', urlWithoutParams, 'because of', method)
    return defaultAdapter(config)
  }

  if (headers['X-CACHE-INVALIDATE']) {
    // console.log(
    //   'Invalidating',
    //   urlWithoutParams,
    //   'because of',
    //   headers['X-CACHE-INVALIDATE']
    // )
    await invalidate(url)
    return defaultAdapter(config)
  }

  const hash = await digestMessage(url)
  let cached = null
  if (hash != null && hash !== undefined && hash !== '') {
    try {
      cached = await db.axiosCache.get(hash)
    } catch (error) {
      console.warn('Failed to get cache entry:', error)
    }
  }

  // if (cached) {
  //   console.log('cache found', url, 'Valid until', new Date(cached?.expiresAt))
  // } else {
  //   console.log('cache not found', url)
  // }

  if (cached && Date.now() <= (cached?.expiresAt ?? 0)) {
    const result = JSON.parse(await decompressString(cached.result))
    return {
      data: result,
      status: 200,
      statusText: 'OK',
      headers: {}, // Custom headers if needed
      config,
      request: null
    }
  }

  if (cached && Date.now() > cached.expiresAt) {
    try {
      if (hash != null && hash !== undefined && hash !== '') {
        await db.axiosCache.delete(hash)
      }
    } catch (error) {
      console.warn('Failed to delete expired cache entry:', error)
    }
  }

  // Proceed with the request
  const response = await defaultAdapter(config)

  // Response Logic
  if (method === 'post' && url === '/v1/auth') {
    setToken(response.data.access_token)
    useJwt.setToken(response.data.access_token)
    useJwt.setRefreshToken(response.data.refresh_token)
    return response
  }

  if (method !== 'get') return response

  if (url.endsWith('logout')) {
    db.axiosCache.clear()
    return response
  }

  for (const item of doNotCache) {
    if (new RegExp(item).test(urlWithoutParams)) {
      return response
    }
  }

  if (!cacheMethod.includes(method)) {
    return response
  }

  if (response.headers['Cache-Control'] === 'no-cache') {
    return response
  }

  let ttl = CACHE_TTL
  if (headers.get('X-CACHE-TTL') || response.headers.get('X-CACHE-TTL')) {
    ttl =
      parseInt(
        headers.get('X-CACHE-TTL') || response.headers.get('X-CACHE-TTL'),
        10
      ) * 1000
  }

  const result = await compressString(response.data)
  const expiresAt = Date.now() + ttl
  if (response.data && response.status < 400 && hash != null && hash !== undefined && hash !== '') {
    // console.log('url', url, 'cached for', ttl, 'ms until', new Date(expiresAt))
    try {
      db.axiosCache.put({ hash, result, expiresAt }, hash)
    } catch (error) {
      console.warn('Failed to cache response:', error)
    }
  }

  return response
}

axiosIns.defaults.adapter = cacheAdapter(xhr)

axiosIns.interceptors.response.use(
  (response) => {
    // Parse storage quota headers from upload responses
    const headers = response.headers || {}
    if (headers['x-storage-warning'] || headers['x-storage-overage']) {
      try {
        const { useStorageQuotaStore } = require('@/pinia/storageQuota')
        const storageStore = useStorageQuotaStore()
        storageStore.updateFromHeaders(headers)
      } catch (e) {
        // Silently fail - store may not be initialized
      }
    }

    // Parse AI token balance headers
    if (headers['x-token-total'] !== undefined) {
      try {
        const { useTokenBalanceStore } = require('@/pinia/tokenBalance')
        const tokenStore = useTokenBalanceStore()
        tokenStore.updateFromHeaders(headers)
      } catch (e) {
        // Silently fail - store may not be initialized
      }
    }

    return response
  },
  (error) => {
    if (error.response?.status === 401 && error.response?.data?.message === 'Unauthenticated.') {
      // Clear authentication data
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      sLocalStorage.removeItem('userData')

      if (router.currentRoute?.name !== 'auth-login') {
        router.replace({ name: 'auth-login' })
      }
    }

    // Handle 403 consent_required (AI consent safety net)
    if (error.response?.status === 403 && error.response?.data?.error_code === 'consent_required') {
      try {
        const { useAiConsentStore } = require('@/pinia/aiConsent')
        const consentStore = useAiConsentStore()
        consentStore.consented = false
        consentStore.checked = false
      } catch (e) {
        // Silently fail
      }
    }

    // Handle 402 Payment Required (subscription & token errors)
    if (error.response?.status === 402) {
      const data = error.response?.data

      // Handle insufficient AI tokens
      if (data?.error_code === 'insufficient_tokens') {
        try {
          const { useTokenBalanceStore } = require('@/pinia/tokenBalance')
          const tokenStore = useTokenBalanceStore()
          if (data.token_balance) {
            tokenStore.setBalance(data.token_balance)
          }
        } catch (e) {
          // Silently fail
        }

        showDedupedToast('insufficient_tokens', () => {
          Vue.nextTick(() => {
            const toast = Vue.prototype.$toast
            if (toast) {
              toast({
                component: ToastificationContent,
                props: {
                  title: 'Token AI Habis',
                  text: data?.message || 'Token AI tidak mencukupi. Silakan beli token tambahan.',
                  icon: 'ZapOffIcon',
                  variant: 'danger',
                },
              })
            }
          })
        })

        return Promise.reject(error)
      }

      // Update subscription status in auth store
      // This will trigger the SuspendedTenantOverlay to show if status is 'suspended'
      if (data?.subscription_status) {
        try {
          const { useAuthStore } = require('@/pinia/auth')
          const authStore = useAuthStore()
          authStore.updateSubscriptionStatus(data.subscription_status)
        } catch (e) {
          // Silently fail - auth store may not be initialized yet
        }
      } else if (data?.error_code === 'subscription_suspended') {
        // If no subscription_status but error_code indicates suspended, force update
        try {
          const { useAuthStore } = require('@/pinia/auth')
          const authStore = useAuthStore()
          authStore.updateSubscriptionStatus('suspended')
        } catch (e) {
          // Silently fail
        }
      }

      // For non-suspended errors (grace period, etc), show a toast (deduped)
      const errorCode = data?.error_code
      if (errorCode && errorCode !== 'subscription_suspended') {
        const message = data?.message || 'Langganan diperlukan untuk mengakses fitur ini.'
        showDedupedToast(errorCode, () => {
          Vue.nextTick(() => {
            const toast = Vue.prototype.$toast
            if (toast) {
              toast({
                component: ToastificationContent,
                props: {
                  title: errorCode === 'subscription_grace_period' ? 'Masa Tenggang' :
                         errorCode === 'subscription_required' ? 'Langganan Diperlukan' : 'Akses Ditolak',
                  text: message,
                  icon: 'AlertTriangleIcon',
                  variant: 'warning',
                },
              })
            }
          })
        })
      }
    }

    // Handle 413 Payload Too Large (storage quota exceeded)
    if (error.response?.status === 413) {
      const data = error.response?.data
      showDedupedToast('storage_quota_exceeded', () => {
        Vue.nextTick(() => {
          const toast = Vue.prototype.$toast
          if (toast) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Kuota Penyimpanan Penuh',
                text: data?.message || 'Penyimpanan telah melebihi batas. Hapus file lama atau tambah kuota.',
                icon: 'HardDriveIcon',
                variant: 'danger',
              },
            })
          }
        })
      })

      // Reload storage usage to reflect blocked state in banners
      try {
        const { useStorageQuotaStore } = require('@/pinia/storageQuota')
        const storageStore = useStorageQuotaStore()
        storageStore.loadUsage()
      } catch (e) {
        // Silently fail
      }
    }

    return Promise.reject(error)
  }
)

// Superadmin request interceptor
axiosIns.interceptors.request.use(
  (config) => {
    // Check if superadmin mode is active
    const superadminData = localStorage.getItem('superadmin_session')
    if (superadminData) {
      try {
        const session = JSON.parse(superadminData)
        if (session.isActive) {
          config.headers['X-Superadmin-Mode'] = 'true'
          
          // Add tenant override if specified
          if (session.currentTenantId && session.currentTenantId !== 0) {
            config.headers['X-Tenant-Override'] = session.currentTenantId.toString()
          }
        }
      } catch (error) {
        console.warn('Failed to parse superadmin session:', error)
      }
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

async function digestMessage(message) {
  if (!message || message === '') {
    console.warn('Empty message provided to digestMessage')
    return ''
  }
  return btoa(sha256(message))
}

async function compressString(inputData) {
  const input = JSON.stringify(inputData)
  const encoder = new TextEncoder()
  const data = encoder.encode(input)

  const cs = new CompressionStream('gzip')
  const writer = cs.writable.getWriter()
  writer.write(data)
  writer.close()

  const compressedStream = cs.readable
  const compressedArrayBuffer = await new Response(
    compressedStream
  ).arrayBuffer()
  const compressedData = new Uint8Array(compressedArrayBuffer)

  let binary = ''
  compressedData.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

async function decompressString(inputData) {
  const binaryString = atob(inputData)
  const compressedData = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    compressedData[i] = binaryString.charCodeAt(i)
  }
  const ds = new DecompressionStream('gzip')
  const writer = ds.writable.getWriter()
  writer.write(compressedData)
  writer.close()

  const decompressedStream = ds.readable
  const decompressedArrayBuffer = await new Response(
    decompressedStream
  ).arrayBuffer()
  const decoder = new TextDecoder()

  return decoder.decode(decompressedArrayBuffer)
}

export function setToken(token) {
  try {
    if (token === undefined) throw new Error('Token is undefined')
  } catch (e) {
    console.error(e)
  }
  axiosIns.defaults.headers.common.Authorization = 'Bearer ' + token
}

export async function invalidate(url) {
  const hash = await digestMessage(url)
  try {
    if (hash != null && hash !== undefined && hash !== '' && await db.axiosCache.get(hash)) {
      await db.axiosCache.delete(hash)
    }
  } catch (e) {
    console.warn('Failed to invalidate cache entry:', e)
  }
}

// Drop every cached GET response. Use when the active tenant context changes
// (e.g. superadmin tenant switch) so stale data from the previous tenant
// can't be served to the new context.
export async function clearAxiosCache() {
  try {
    await db.axiosCache.clear()
  } catch (e) {
    console.warn('Failed to clear axios cache:', e)
  }
}

Vue.prototype.$http = axiosIns

export default axiosIns
